import React from 'react';
import { useQuery } from 'react-query';
import * as API from 'api';

import { withStyles } from '@material-ui/core/styles';
import MuiDialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import MuiButton from '@material-ui/core/Button';
import MuiTypography from '@material-ui/core/Typography';
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiChip from '@material-ui/core/Chip';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiExpandMoreIcon from '@material-ui/icons/ExpandMore';

const Releases = API.Releases();

const styles = theme => ({
  section: {
    marginTop: theme.spacing(3),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(1, 3),
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  value: {
    wordBreak: 'break-word',
  },
  lyrics: {
    whiteSpace: 'pre-wrap',
    maxHeight: 200,
    overflow: 'auto',
  },
  trackSummary: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    width: '100%',
  },
  muted: {
    color: theme.palette.text.secondary,
  },
});

const show = value => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatDate = value => (value ? new Date(value).toLocaleString() : '—');

const partiesText = parties =>
  parties.length ? parties.map(party => `${party.name || '?'} (${party.roles.join(', ')})`).join('; ') : '—';

const Fields = withStyles(styles)(({ classes, fields }) => (
  <div className={classes.grid}>
    {fields.map(([label, value]) => (
      <div key={label}>
        <div className={classes.label}>{label}</div>
        <div className={classes.value}>{show(value)}</div>
      </div>
    ))}
  </div>
));

const Section = withStyles(styles)(({ classes, title, children }) => (
  <div className={classes.section}>
    <MuiTypography variant="h6" gutterBottom>
      {title}
    </MuiTypography>
    {children}
  </div>
));

function SimpleTable({ columns, rows, empty }) {
  if (!rows.length) return <MuiTypography variant="body2">{empty}</MuiTypography>;
  return (
    <MuiTable size="small">
      <MuiTableHead>
        <MuiTableRow>
          {columns.map(([label]) => (
            <MuiTableCell key={label}>{label}</MuiTableCell>
          ))}
        </MuiTableRow>
      </MuiTableHead>
      <MuiTableBody>
        {rows.map((row, index) => (
          <MuiTableRow key={index}>
            {columns.map(([label, render]) => (
              <MuiTableCell key={label}>{show(render(row))}</MuiTableCell>
            ))}
          </MuiTableRow>
        ))}
      </MuiTableBody>
    </MuiTable>
  );
}

const TrackDetails = withStyles(styles)(({ classes, track }) => (
  <MuiAccordion TransitionProps={{ unmountOnExit: true }}>
    <MuiAccordionSummary expandIcon={<MuiExpandMoreIcon />}>
      <div className={classes.trackSummary}>
        <strong>
          {track.disc}.{track.sequence}
        </strong>
        <span>{track.title}</span>
        <span className={classes.muted}>{track.isrc || 'no ISRC yet'}</span>
      </div>
    </MuiAccordionSummary>
    <MuiAccordionDetails style={{ display: 'block' }}>
      <Fields
        fields={[
          ['Track ID', track.trackId],
          ['Title', track.title],
          ['Version', track.subTitle],
          ['Display artist', track.displayArtistName],
          ['ISRC', track.isrc],
          ['Atmos ISRC', track.atmosIsrc],
          ['Genre', track.genre],
          ['Language', track.language],
          ['Parental warning', track.parentalWarning],
          ['Duration', track.duration],
          ['Price tier', track.priceTier],
          ['Instant gratification', track.instantGratDate],
          ['Immersive audio', track.hasImmersiveAudio],
          ['Royalties blocked', track.blockRoyalties],
          ['Platform fee', track.platformFees],
        ]}
      />
      <Section title="Artists and credits">
        <Fields
          fields={[
            ['Artists', partiesText(track.artists)],
            ['Contributors', partiesText(track.contributors)],
          ]}
        />
      </Section>
      <Section title="Splits">
        <SimpleTable
          empty="No splits."
          rows={track.splits}
          columns={[
            ['Payee', split => split.payee],
            ['%', split => split.percentage],
            ['Roles', split => split.roles],
            ['Publisher', split => split.publisher],
          ]}
        />
      </Section>
      <Section title="Files">
        <SimpleTable
          empty="No files."
          rows={track.files}
          columns={[
            ['Type', file => file.type],
            ['File name', file => file.fileName],
            ['Storage key', file => file.fileKey],
            ['Size (MB)', file => (file.bytes ? (file.bytes / 1024 / 1024).toFixed(1) : null)],
            [
              'Source',
              file => (file.generatedForSubmission ? `generated (#${file.generatedForSubmission})` : 'uploaded'),
            ],
            ['Created', file => formatDate(file.createdAt)],
          ]}
        />
      </Section>
      {track.lyrics && (
        <Section title="Lyrics">
          <div className={classes.lyrics}>{track.lyrics}</div>
        </Section>
      )}
    </MuiAccordionDetails>
  </MuiAccordion>
));

const ReleaseDetailsDialog = ({ open, onClose, releaseId }) => {
  // Fetched only while the dialog is open, so the release list itself stays light.
  const { data, isLoading, error } = useQuery(['releaseDetails', releaseId], () => Releases.details(releaseId), {
    enabled: open && !!releaseId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const release = data?.release;

  return (
    <MuiDialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <MuiDialogTitle>
        {release ? `${release.title} (#${release.id})` : 'Release details'}
        {data?.aepComanager && (
          <MuiChip size="small" color="primary" variant="outlined" label="aep-comanager" style={{ marginLeft: 8 }} />
        )}
      </MuiDialogTitle>
      <MuiDialogContent dividers>
        {isLoading && <MuiCircularProgress />}
        {error && <MuiTypography color="error">Could not load release details.</MuiTypography>}
        {data && (
          <>
            <Fields
              fields={[
                ['Group', data.group ? `${data.group.name} (#${data.group.id})` : null],
                ['Display artist', release.displayArtistName],
                ['Label', release.displayLabelName],
                ['Type', release.type],
                ['Version', release.subTitle],
                ['UPC', release.upc],
                ['Catalog number', release.catalogNumber],
                ['Genre', release.genre],
                ['Language', release.language],
                ['Copyright year', release.copyrightYear],
                ['℗ line', release.pLine],
                ['© line', release.cLine],
                ['Release date', release.releaseDate],
                ['Original release date', release.originalReleaseDate],
                ['Pre-order date', release.preOrderDate],
                ['Pre-save date', release.preSaveDate],
                ['Timed release', release.isTimedRelease],
                ['Price tier', release.priceTier],
                ['Territories', release.territories],
                ['DSPs', release.dsps],
                ['Archived', release.archived],
                ['Created', formatDate(release.createdAt)],
                ['Updated', formatDate(release.updatedAt)],
              ]}
            />

            {data.aepComanager && (
              <Section title="aep-comanager">
                <Fields
                  fields={[
                    ['Release ID', data.aepComanager.releaseId],
                    ['Workspace ID', data.aepComanager.workspaceId],
                    ['Revision', data.aepComanager.revision],
                    ['Files synced', data.aepComanager.assetsSynced],
                  ]}
                />
              </Section>
            )}

            <Section title="Release artists">
              <MuiTypography variant="body2">{partiesText(data.artists)}</MuiTypography>
            </Section>

            <Section title={`Tracks (${data.tracks.length})`}>
              {data.tracks.length ? (
                data.tracks.map(track => <TrackDetails key={track.trackId} track={track} />)
              ) : (
                <MuiTypography variant="body2">No tracks.</MuiTypography>
              )}
            </Section>

            <Section title="Deliveries">
              <SimpleTable
                empty="Not delivered yet."
                rows={data.deliveries}
                columns={[
                  ['DSP', job => job.dsp],
                  ['Type', job => job.ernType],
                  ['Status', job => job.status],
                  ['Submission', job => job.submissionId],
                  ['Started', job => formatDate(job.startedAt)],
                  ['Completed', job => formatDate(job.completedAt)],
                  ['Error', job => job.error],
                ]}
              />
            </Section>

            <Section title="Submissions">
              <SimpleTable
                empty="No submissions."
                rows={data.submissions}
                columns={[
                  ['ID', entry => entry.submissionId],
                  ['Status', entry => entry.status],
                  ['Takedown', entry => entry.isTakedown],
                  ['DSPs', entry => entry.dsps],
                  ['Created', entry => formatDate(entry.createdAt)],
                ]}
              />
            </Section>
          </>
        )}
      </MuiDialogContent>
      <MuiDialogActions>
        <MuiButton onClick={onClose}>Close</MuiButton>
      </MuiDialogActions>
    </MuiDialog>
  );
};

export default ReleaseDetailsDialog;
